import type { EnergyTypes, Rotation, RotationType } from '@libs/interfaces/interfaces'
import i18n from '@libs/internationalization'
import ColumnChart from '@libs/models/charts/columnChart'
import PieChart from '@libs/models/charts/pieChart'
import { energyTypeColors } from '@libs/models/energyTypeColors'
import { formatAxisNumber, formatCurrency, standardDataTransformer } from '@libs/models/formatters'
import Highcharts, { type DataLabelsFormatterCallbackFunction } from 'highcharts'

const deltaComparisonColumnConfig = (color: string): Highcharts.SeriesOptionsType => ({
	color,
	dataLabels: {
		enabled: false,
	},
	name: 'deltaColumn',
	opacity: 0.8,
	showInLegend: false,
	stacking: 'normal',
	type: 'column', // Highchart.options requires type to be set for other properties to exist
})

const comparisonColumnConfig = (color: string): Highcharts.SeriesOptionsType => ({
	color,
	dataLabels: {
		enabled: false,
		formatter: formatAxisNumber as DataLabelsFormatterCallbackFunction,
	},
	name: 'comparisonColumn',
	stacking: 'normal',
	type: 'column', // Highchart.options requires type to be set for other properties to exist
})

const pieConfig: Highcharts.Options = {
	series: [
		{
			dataLabels: {
				formatter: formatAxisNumber as DataLabelsFormatterCallbackFunction,
			},
			type: 'pie',
		},
	],
}

const electricityConfig: Highcharts.Options = {
	series: [
		{
			color: energyTypeColors.current.electricity,
			dataLabels: {
				formatter: formatAxisNumber as DataLabelsFormatterCallbackFunction,
			},
			name: 'primaryColumn', // Highchart.options requires type to be set for other properties to exist
			type: 'column',
		},
		comparisonColumnConfig(energyTypeColors.historic.electricity),
		deltaComparisonColumnConfig(energyTypeColors.historic.electricity),
	],
}

const solarConfig: Highcharts.Options = {
	series: [
		{
			color: energyTypeColors.current.solar,
			dataLabels: {
				formatter: formatAxisNumber as DataLabelsFormatterCallbackFunction,
			},
			name: 'primaryColumn', // Highchart.options requires type to be set for other properties to exist
			type: 'column',
		},
		comparisonColumnConfig(energyTypeColors.historic.solar),
		deltaComparisonColumnConfig(energyTypeColors.historic.solar),
	],
}

const windConfig: Highcharts.Options = {
	series: [
		{
			color: energyTypeColors.current.wind,
			dataLabels: {
				formatter: formatAxisNumber as DataLabelsFormatterCallbackFunction,
			},
			name: 'primaryColumn', // Highchart.options requires type to be set for other properties to exist
			type: 'column',
		},
		comparisonColumnConfig(energyTypeColors.historic.wind),
		deltaComparisonColumnConfig(energyTypeColors.historic.wind),
	],
}

const waterConfig: Highcharts.Options = {
	series: [
		{
			color: energyTypeColors.current.water,
			dataLabels: {
				formatter: formatAxisNumber as DataLabelsFormatterCallbackFunction,
			},
			name: 'primaryColumn', // Highchart.options requires type to be set for other properties to exist
			type: 'column',
		},
		comparisonColumnConfig(energyTypeColors.historic.water),
		deltaComparisonColumnConfig(energyTypeColors.historic.water),
	],
}

const heatConfig: Highcharts.Options = {
	series: [
		{
			color: energyTypeColors.current.heat,
			dataLabels: {
				formatter: formatAxisNumber as DataLabelsFormatterCallbackFunction,
			},
			name: 'primaryColumn', // Highchart.options requires type to be set for other properties to exist
			type: 'column',
		},
		comparisonColumnConfig(energyTypeColors.historic.heat),
		deltaComparisonColumnConfig(energyTypeColors.historic.heat),
	],
}

const chargingPointConfig: Highcharts.Options = {
	series: [
		{
			color: energyTypeColors.current.chargingPoint,
			dataLabels: {
				formatter: formatAxisNumber as DataLabelsFormatterCallbackFunction,
			},
			name: 'primaryColumn', // Highchart.options requires type to be set for other properties to exist
			type: 'column',
		},
		comparisonColumnConfig(energyTypeColors.historic.chargingPoint),
		deltaComparisonColumnConfig(energyTypeColors.historic.chargingPoint),
	],
}

export type Charts = {
	[index: string]: {
		config: Highcharts.Options;
		chart: Function;
		dataTransformer: Function;
	};
};

export const charts: Charts = {
	chargingPoint: {
		chart: ColumnChart,
		config: chargingPointConfig,
		dataTransformer: standardDataTransformer,
	},
	electricity: {
		chart: ColumnChart,
		config: electricityConfig,
		dataTransformer: standardDataTransformer,
	},
	heat: {
		chart: ColumnChart,
		config: heatConfig,
		dataTransformer: standardDataTransformer,
	},
	solar: {
		chart: ColumnChart,
		config: solarConfig,
		dataTransformer: standardDataTransformer,
	},
	total: {
		chart: PieChart,
		config: pieConfig,
		dataTransformer: (data: Rotation) =>
			data.chart.categories.labels.map((category: string, index) => {
				const value = data.chart.currentData[index]
				return {
					color: energyTypeColors.current[category as keyof EnergyTypes['current']],
					formatter: data.consumptionType === 'cost' && value !== undefined ? formatCurrency(data.unit, value) : () => null,
					name: i18n.global.t(`sharedTexts.energyTypes.${category}`),
					unit: data.unit,
					y: value ?? 0,
				}
			}),
	},
	water: {
		chart: ColumnChart,
		config: waterConfig,
		dataTransformer: standardDataTransformer,
	},
	wind: {
		chart: ColumnChart,
		config: windConfig,
		dataTransformer: standardDataTransformer,
	},
}

export const findChart = (rotationType: RotationType, energyType: string | string[]) =>
	charts[rotationType === 'single' ? (energyType as string) : rotationType]

export const mergeSeries = (config: Highcharts.Options, defaultConfig: Highcharts.Options, data: unknown[][]) => {
	if (config?.series && defaultConfig.series) {
		// Highcharts does not like to merge series
		const series = defaultConfig.series
		const seriesTemplate = defaultConfig.series[0]

		data.forEach((seriesData, i) => {
			const configSeries = config.series?.[i]
			if (!(series && configSeries)) return

			series[i] = Highcharts.merge(seriesTemplate, configSeries)

			// eslint-disable-next-line
			// @ts-expect-error
			series[i].data = seriesData
		})

		defaultConfig.series.reverse()
	}
}
